import { Suspense, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import { useControls } from 'leva';
import { uTime, uDeltaTime, uGlobalHueShift } from '../core/shaders/uniforms';
import { useGameStore } from '../core/store/gameStore';
import { AsyncCompile } from '@core';
import { Character } from './character';
import { MeadowWorld } from './yosemite/MeadowWorld';
import { ImportedScene } from './import/ImportedScene';

export function WorldController() {
    const setActiveTargets = useGameStore((state) => state.setActiveTargets);
    const setComponentReady = useGameStore((state) => state.setComponentReady);

    const debugMode = new URLSearchParams(window.location.search).get('debug') === 'true';
    const urlDisable = new URLSearchParams(window.location.search);
    const noWorld = urlDisable.get('noworld') === '1';
    const noCharacter = urlDisable.get('nocharacter') === '1';

    // Enable eruda console only in debug mode (?debug=true)
    useEffect(() => {
        if (!debugMode) return;

        let cancelled = false;

        (async () => {
            try {
                const mod = await import('eruda');
                if (cancelled) return;
                const eruda: any = (mod as any).default ?? mod;
                if (typeof eruda.init === 'function') {
                    eruda.init();
                }
            } catch (e) {
                console.error('Failed to initialize eruda', e);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [debugMode]);

    const { enableWorld, enableCharacter } = useControls('Game.Content', {
        enableWorld: {
            value: true,
            label: '🌍 World visible',
        },
        enableCharacter: { value: true, label: '👤 Character' },
    }, { collapsed: true });

    const { timeScale, globalHue } = useControls('Game.System', {
        timeScale: { value: 1.0, min: 0.0, max: 2.0, label: 'Game Speed' },
        globalHue: { value: 0.0, min: 0.0, max: 1.0, label: 'Global Hue' },
    });

    useEffect(() => {
        const targets: string[] = [];
        if (enableWorld && !noWorld) targets.push('yosemite');
        if (enableCharacter && !noCharacter) targets.push('character');
        setActiveTargets(targets);
    }, [enableWorld, enableCharacter, noWorld, noCharacter, setActiveTargets]);

    useFrame((_state, rawDelta) => {
        const delta = Math.min(rawDelta, 0.1);
        uGlobalHueShift.value = globalHue;
        uTime.value += delta * timeScale;
        uDeltaTime.value = delta * timeScale;
    });

    return <>
        <Suspense fallback={null}>
            {/* The open meadow world - flat grass near spawn, hazy far ridges */}
            {enableWorld && !noWorld && <MeadowWorld />}

            {/* User-imported SketchUp models (.skp parsed in-browser, .glb/.gltf) */}
            {enableWorld && !noWorld && <ImportedScene />}

            {/* The astronaut wanderer */}
            <AsyncCompile id="character" onReady={setComponentReady} debug={debugMode}>
                <Character position={[0, 0, 0]} scale={1} visible={enableCharacter && !noCharacter} />
            </AsyncCompile>
        </Suspense>
    </>
}
